import { AfterViewInit, Directive, ElementRef, inject, input, Renderer2, viewChild } from '@angular/core';
import { TrustpilotService } from './trustpilot-widget.service';
import { App } from '@df/core/app';

@Directive()
export abstract class TrustpilotWidgetComponentDef implements AfterViewInit {
    readonly templateId = input.required<string>();
    readonly businessunitId = input.required<string>();
    readonly url = input<string>('');
    readonly locale = input<string>('en-GB');
    readonly styleHeight = input<string>('');
    readonly styleWidth = input<string>('');
    readonly theme = input<string>('');
    readonly tags = input<string>('');
    readonly stars = input<string>('');
    readonly starColor = input<string>('');
    readonly noReviews = input<string>('');
    readonly scrollToList = input<string>('');
    /**
     * WARNING:
     * When using product reviews you must provide name too!
     * We will get no structural data from TP without it.
     */
    readonly sku = input<string>('');
    /**
     * WARNING:
     * see SKU note above
     */
    readonly name = input<string>('');
    readonly fontFamily = input<string>('');
    readonly reviewLanguages = input<string>('');
    readonly styleAlignment = input<string>('');
    readonly availability = input<string>('');
    readonly price = input<number | undefined>(undefined);

    protected readonly trustpilotService = inject(TrustpilotService);

    private readonly renderer2 = inject(Renderer2);
    private readonly container = viewChild<ElementRef<HTMLDivElement>>('container');
    private readonly app = inject(App);
    ngAfterViewInit(): void {
        this.update();
    }

    protected update(): void {
        const trustboxRef = this.trustboxRef;
        if (trustboxRef) {
            this.renderer2.setAttribute(trustboxRef, 'data-locale', this.locale() || 'en-GB');
            this.renderer2.setAttribute(trustboxRef, 'data-template-id', this.templateId());
            this.renderer2.setAttribute(trustboxRef, 'data-businessunit-id', this.businessunitId());

            const styleHeight = this.styleHeight();
            const styleWidth = this.styleWidth();
            const theme = this.theme();
            const starColor = this.starColor();
            const scrollToList = this.scrollToList();
            const noReviews = this.noReviews();
            const sku = this.sku();
            const name = this.name();
            const fontFamily = this.fontFamily();
            const reviewLanguages = this.reviewLanguages();
            const styleAlignment = this.styleAlignment();
            const tags = this.tags();
            const stars = this.stars();
            const availability = this.availability();
            const currency = this.app.data.currency.base;
            const price = this.price();

            if (price) {
                this.renderer2.setAttribute(trustboxRef, 'data-price', price.toString());
            }

            if (currency) {
                this.renderer2.setAttribute(trustboxRef, 'data-price-currency', currency);
            }

            if (availability) {
                this.renderer2.setAttribute(trustboxRef, 'data-availability', availability);
            }

            if (styleHeight) {
                this.renderer2.setAttribute(trustboxRef, 'data-style-height', styleHeight);
            }
            if (styleWidth) {
                this.renderer2.setAttribute(trustboxRef, 'data-style-width', styleWidth);
            }
            if (theme) {
                this.renderer2.setAttribute(trustboxRef, 'data-theme', theme);
            }
            if (starColor) {
                this.renderer2.setAttribute(trustboxRef, 'data-star-color', starColor);
            }
            if (scrollToList) {
                this.renderer2.setAttribute(trustboxRef, 'data-scroll-to-list', scrollToList);
            }
            if (noReviews) {
                this.renderer2.setAttribute(trustboxRef, 'data-no-reviews', noReviews);
            }
            if (sku) {
                this.renderer2.setAttribute(trustboxRef, 'data-sku', sku);
            }
            if (name) {
                this.renderer2.setAttribute(trustboxRef, 'data-name', name);
            }
            if (fontFamily) {
                this.renderer2.setAttribute(trustboxRef, 'data-font-family', fontFamily);
            }
            if (reviewLanguages) {
                this.renderer2.setAttribute(trustboxRef, 'data-review-languages', reviewLanguages);
            }
            if (styleAlignment) {
                this.renderer2.setAttribute(trustboxRef, 'data-style-alignment', styleAlignment);
            }
            if (tags) {
                this.renderer2.setAttribute(trustboxRef, 'data-tags', tags);
            }
            if (stars) {
                this.renderer2.setAttribute(trustboxRef, 'data-stars', stars);
            }
            this.trustpilotService.load(() => {
                if (this.trustpilotService.api) {
                    this.trustpilotService.api.loadFromElement(trustboxRef);
                }
            });
        }
    }

    protected get trustboxRef(): HTMLDivElement | undefined {
        return this.container()?.nativeElement;
    }
}
